import React from 'react';
import {
  Typography,
  Container,
  Box,
  Divider,
  Card,
  CardActionArea,
  CardContent,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { getCurrentBusinessConfig } from '../../firebase/firebase-delegate';
import { BusinessConfig } from '../../types';
import {
  AccessTime,
  ManageAccounts,
  DesignServices,
  PhotoLibrary,
  AccountBalanceWallet,
  Subscriptions,
  SupportAgent,
  AccountCircle,
  Home,
} from '@mui/icons-material';
import { useAdminData } from '../../contexts/AdminDataContext';
import { resolveBookingUxConfig } from '../../utils/bookingUxConfig';

const ActionCard: React.FC<{
  title: string;
  Icon: React.ElementType;
  onClick: () => void;
}> = ({ title, Icon, onClick }) => {
  return (
    <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
      <CardActionArea
        onClick={onClick}
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'stretch',
        }}
      >
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            py: 3,
            width: '100%',
            // Portrait feel
            minHeight: 160,
          }}
        >
          <Icon sx={{ fontSize: 56, color: 'text.secondary' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 475, textAlign: 'center' }}>
            {title}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

type SectionAction = {
  title: string;
  Icon: React.ElementType;
  onClick: () => void;
};

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();

  const { currentEmployeeQuery: employeeQuery } = useAdminData();

  const businessConfigQuery = useQuery<BusinessConfig | null>({
    queryKey: ['businessConfig'],
    queryFn: () => getCurrentBusinessConfig(),
  });

  const isAdmin = employeeQuery.data?.admin === true;
  const bookingUxConfig = resolveBookingUxConfig(businessConfigQuery.data);
  const isResourceMode = bookingUxConfig.bookingModel === 'resource';
  const resourcePlural = bookingUxConfig.terminology.resourcePlural;
  const isOwner = businessConfigQuery.data?.paymentInfo?.owner === employeeQuery.data?.id;
  const useBusinessStripe = Boolean(businessConfigQuery.data?.payoutInfo?.useBusinessStripeAccount);
  const paymentsEnabled = businessConfigQuery.data?.features?.enablePayments || false;
  const showPaymentPortal = paymentsEnabled && (isOwner || !useBusinessStripe);
  const schedulingActions: SectionAction[] = !isResourceMode
    ? [
      {
        title: 'My Work Hours',
        Icon: AccessTime,
        onClick: () => navigate('/admin/edit-work-hours'),
      },
    ]
    : [];

  return (
    <Container>
      <Box sx={{ mt: 4 }}>
        {/* Scheduling */}
        {schedulingActions.length > 0 && (
          <>
            <Divider sx={{ width: '100%', mb: 2 }} textAlign="center">
              <Typography variant="h5" sx={{ px: 1, fontWeight: 600 }}>
                Scheduling
              </Typography>
            </Divider>
            <Grid2 container spacing={2} justifyContent="center">
              {schedulingActions.map((action) => (
                <Grid2 key={action.title} size={{ xs: 12, sm: 6, md: 4 }}>
                  <ActionCard
                    title={action.title}
                    Icon={action.Icon}
                    onClick={action.onClick}
                  />
                </Grid2>
              ))}
            </Grid2>
          </>
        )}

        {/* Team */}
        {isAdmin && (
          <>
            <Divider sx={{ width: '100%', my: 3 }} textAlign="center">
              <Typography variant="h5" sx={{ px: 1, fontWeight: 600 }}>
                {resourcePlural}
              </Typography>
            </Divider>
            <Grid2 container spacing={2} justifyContent="center">
              <Grid2 size={{ xs:12, sm:6, md:4 }}>
                <ActionCard
                  title={`Manage ${resourcePlural}`}
                  Icon={ManageAccounts}
                  onClick={() => navigate('/admin/manage-employees')}
                />
              </Grid2>
            </Grid2>
          </>
        )}

        {/* Website */}
        {isAdmin && (
          <>
            <Divider sx={{ width: '100%', my: 3 }} textAlign="center">
              <Typography variant="h5" sx={{ px: 1, fontWeight: 600 }}>
                Website
              </Typography>
            </Divider>
            <Grid2 container spacing={2} justifyContent="center">
              <Grid2 size={{ xs:12, sm:6, md:4 }}>
                <ActionCard
                  title="Manage Services"
                  Icon={DesignServices}
                  onClick={() => navigate('/admin/manage-services')}
                />
              </Grid2>
              <Grid2 size={{ xs:12, sm:6, md:4 }}>
                <ActionCard
                  title="Photo Gallery"
                  Icon={PhotoLibrary}
                  onClick={() => navigate('/admin/gallery')}
                />
              </Grid2>
              <Grid2 size={{ xs:12, sm:6, md:4 }}>
                <ActionCard
                  title="Business Details"
                  Icon={Home}
                  onClick={() => navigate('/admin/website-details')}
                />
              </Grid2>
            </Grid2>
          </>
        )}

        {/* Billing */}
        {showPaymentPortal && (
          <>
            <Divider sx={{ width: '100%', my: 3 }} textAlign="center">
              <Typography variant="h5" sx={{ px: 1, fontWeight: 600 }}>
                Billing
              </Typography>
            </Divider>
            <Grid2 container spacing={2} justifyContent="center">
              <Grid2 size={{ xs:12, sm:6, md:4 }}>
                <ActionCard
                  title="Payment Portal"
                  Icon={AccountBalanceWallet}
                  onClick={() => navigate('/admin/payment-portal')}
                />
              </Grid2>
            </Grid2>
          </>
        )}

        {/* Account */}
        <Divider sx={{ width: '100%', my: 3 }} textAlign="center">
          <Typography variant="h5" sx={{ px: 1, fontWeight: 600 }}>
            Account
          </Typography>
        </Divider>
        <Grid2 container spacing={2} justifyContent="center">
          <Grid2 size={{ xs:12, sm:6, md:4 }}>
            <ActionCard
              title="My Profile"
              Icon={AccountCircle}
              onClick={() => navigate('/admin/my-profile')}
            />
          </Grid2>
          {isAdmin && isOwner && (
            <Grid2 size={{ xs:12, sm:6, md:4 }}>
              <ActionCard
                title="Manage Scheduify Subscription"
                Icon={Subscriptions}
                onClick={() => navigate('/admin/manage-subscription')}
              />
            </Grid2>
          )}
          <Grid2 size={{ xs:12, sm:6, md:4 }}>
            <ActionCard
              title="Contact Support"
              Icon={SupportAgent}
              onClick={() => navigate('/admin/contact-support')}
            />
          </Grid2>
        </Grid2>
      </Box>
    </Container>
  );
};

export default SettingsPage;
